import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "../../lib/utils";

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "jobsite:data-[state=open]:animate-in jobsite:data-[state=closed]:animate-out jobsite:data-[state=closed]:fade-out-0 jobsite:data-[state=open]:fade-in-0 jobsite:fixed jobsite:inset-0 jobsite:z-50 jobsite:bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  /** Override the scrim, e.g. a darker one for a photo viewer. */
  overlayClassName?: string;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "jobsite:bg-background jobsite:data-[state=open]:animate-in jobsite:data-[state=closed]:animate-out jobsite:data-[state=closed]:fade-out-0 jobsite:data-[state=open]:fade-in-0 jobsite:data-[state=closed]:zoom-out-95 jobsite:data-[state=open]:zoom-in-95 jobsite:fixed jobsite:top-[50%] jobsite:left-[50%] jobsite:z-50 jobsite:grid jobsite:w-full jobsite:max-w-[calc(100%-2rem)] jobsite:translate-x-[-50%] jobsite:translate-y-[-50%] jobsite:gap-4 jobsite:rounded-lg jobsite:border jobsite:p-6 jobsite:shadow-lg jobsite:duration-200 jobsite:sm:max-w-lg",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="jobsite:ring-offset-background jobsite:focus:ring-ring jobsite:data-[state=open]:bg-accent jobsite:data-[state=open]:text-muted-foreground jobsite:absolute jobsite:top-4 jobsite:right-4 jobsite:rounded-xs jobsite:opacity-70 jobsite:transition-opacity jobsite:hover:opacity-100 jobsite:focus:ring-2 jobsite:focus:ring-offset-2 jobsite:focus:outline-hidden jobsite:disabled:pointer-events-none jobsite:[&_svg]:pointer-events-none jobsite:[&_svg]:shrink-0 jobsite:[&_svg:not([class*=size-])]:size-4"
          >
            <XIcon />
            <span className="jobsite:sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "jobsite:flex jobsite:flex-col jobsite:gap-2 jobsite:text-center jobsite:sm:text-left",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "jobsite:flex jobsite:flex-col-reverse jobsite:gap-2 jobsite:sm:flex-row jobsite:sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("jobsite:text-lg jobsite:leading-none jobsite:font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("jobsite:text-muted-foreground jobsite:text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
